import React from "react";
import { formatValue, getStatusColor } from "../utils/helpers";

/**
 * InfoSection Component
 * A flexible layout wrapper for displaying labeled information blocks in a responsive grid.
 *
 * @param {string} title - The section title.
 * @param {string} [subtitle] - Optional subtitle shown below the title.
 * @param {React.ReactNode} children - The content (usually <InfoItem> components).
 * @param {number} [columns=2] - Number of columns for the grid layout (1–4 recommended).
 */
export const InfoSection = ({ title, subtitle, children, columns = 2 }) => {
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-3 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-4 lg:grid-cols-4",
  }[columns] || "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2";

  return (
    <section className="mb-8">
      <header className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </header>
      <dl className={`grid gap-4 ${gridCols}`}>{children}</dl>
    </section>
  );
};


/**
 * InfoItem Component
 * Displays a label-value pair, optionally styled as a status badge.
 *
 * @param {string} label - The label text.
 * @param {string|number|null} value - The value to display.
 * @param {boolean} [isStatus=false] - Whether to display the value as a status badge.
 */
export const InfoItem = ({ label, value, isStatus = false }) => {
  const baseClasses =
    "bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow";
  const labelClasses =
    "text-xs font-medium text-gray-500 uppercase tracking-wide mb-1";
  const valueClasses = isStatus
    ? `px-2 py-1 rounded-full text-xs font-semibold inline-block ${getStatusColor(
        value
      )}`
    : "text-sm font-medium text-gray-900";

  return (
    <div className={baseClasses}>
      <dt className={labelClasses}>{label}</dt>
      <dd className={valueClasses}>{isStatus ? value : formatValue(value)}</dd>
    </div>
  );
};
